import { isRestrictedCountry } from "@/helpers/country";
import Vue from "vue";
import Vuex from "vuex";
import modalModule from "./modules/modal.store.js";
import mailModule from "./modules/mail.store.js";
import siteModule from "./modules/site.store.js";
import paymentModule from "./modules/payment.store.js";
import domainModule from "./modules/domain.store.js";
import referralModule from "./modules/referral.store.js";
import bundleModule from "./modules/bundle.store.js";
import entriModule from "./modules/entri.store.js";
import bannerModule from "./modules/banner.store";
import { getActiveProducts, getProductsFromCurrentPlan } from "@/helpers/product.js";
import {
  getBundleBillingCycle,
  getBundleExpiresOn,
  getBundleFirstBillingCycleUntil,
  getBundleRenewalBillingCycle,
  isOrderLevelBillingEnabled,
} from "@/helpers/bundle-billing.helper";
import { BUNDLE_PLAN_TYPE } from "@/helpers/const";

Vue.use(Vuex);

const getDefaultState = () => ({
  theme: "defaultTheme",
  /** Configuration for default vs iframe(widget) views */
  view: {},
  locale: "en",
  partnerId: null,
  partnerName: null,
  isAppBannerVisible: false,
  currentDomainMailboxDetails: [],
  dkimUnverifiedKey: {},
  dkimVerifiedKeys: [],
  /** All products for which trials are enabled. */
  trials: {},

  brandDetails: {},
  currentPlanDetails: {},
  /** Processed calculate-charge (RenewBundle) response, used for renewal amounts/pricing. */
  renewalChargeDetails: {},

  purchaseAndBillingDetails: {},
  showDomainAdminPopup: false,
  partnerInfo: {},
  showAutoRenew: false,
});

const stateModules = {
  modal: modalModule,
  mail: mailModule,
  site: siteModule,
  domain: domainModule,
  referral: referralModule,
  payment: paymentModule,
  bundle: bundleModule,
  entri: entriModule,
  banner: bannerModule,
};

export default new Vuex.Store({
  modules: stateModules,
  /* Global State */
  state: () => getDefaultState(),
  /* Global Getters */
  getters: {
    theme: state => state.theme,
    trials: state => state.trials,
    view: state => state.view,
    locale: state => state.locale,
    products(state, getters) {
      return getProductsFromCurrentPlan(getters.currentPlanDetails);
    },
    activeProducts(state, getters) {
      return getActiveProducts(getters.currentPlanDetails);
    },
    isAllProductsActive(state, getters) {
      return getters.products.every(p => getters.activeProducts.includes(p));
    },
    isAppBannerVisible: state => state.isAppBannerVisible,
    partnerId: state => state.partnerId,
    partnerName: state => state.partnerName,
    flags: state => state.partnerInfo?.flags,
    /**
     * Partner-level billing switch from partner info. `?? true`, not `||`, so an
     * explicit `false` (partner disabled payments) is preserved while a missing
     * value defaults to enabled — mirrors App.vue's `paymentEnabled` derivation.
     * Note: this reflects the partner config only, not the transient
     * `makePaymentUrl === "#"` runtime override App.vue applies to its own copy.
     */
    paymentEnabled: state => state.partnerInfo?.paymentEnabled ?? true,
    brandDetails: state => state.brandDetails,
    currentPlanDetails: state => state.currentPlanDetails,
    /**
     * Processed renewal charge details (derived from the calculate-charge API).
     * Source of truth for renewal amounts/pricing on the billing & subscription screens.
     */
    renewalChargeDetails: state => state.renewalChargeDetails,
    /**
     * True once the current plan's details have loaded.
     */
    isCurrentPlanFetched: state => Object.keys(state.currentPlanDetails || {}).length > 0,
    /**
     * Bundle level billing props are returned by `get-current-plan` only while the bundle
     * is billed as a whole. With order level billing they are absent, so every consumer
     * must handle `null`. See helpers/bundle-billing.helper for details.
     */
    isOrderLevelBillingEnabled: state => isOrderLevelBillingEnabled(state.currentPlanDetails),
    bundleBillingCycle: state => getBundleBillingCycle(state.currentPlanDetails),
    bundleRenewalBillingCycle: state => getBundleRenewalBillingCycle(state.currentPlanDetails),
    bundleFirstBillingCycleUntil: state =>
      getBundleFirstBillingCycleUntil(state.currentPlanDetails),
    bundleExpiresOn: state => getBundleExpiresOn(state.currentPlanDetails),
    currency: (state, getters) => getters.currentPlanDetails?.currency,
    currentDomainMailboxDetails: state => state.currentDomainMailboxDetails,
    /**
     * A mailbox's own numeric account id, by its address, for call sites that only have the address
     * to hand. Mailbox medusa events are `flock_order` (keyed by the order) and carry the mailbox
     * as the `account_id` attribute, which this resolves. Undefined when the mailbox is not in the
     * current domain's list — e.g. one created moments ago, before the list refetches, or a
     * forwarder (those come from `data.forwarders`, not `data.accounts`).
     */
    accountIdByEmail: state => email => {
      /* email addresses are case-insensitive, and the stored casing need not match the caller's */
      const address = String(email || "").toLowerCase();
      /* without this, a missing address would match a mailbox that has no address either */
      if (!address) return undefined;
      return (state.currentDomainMailboxDetails || []).find(
        mailbox => String(mailbox.email || "").toLowerCase() === address
      )?.id;
    },
    purchaseAndBillingDetails: state => state.purchaseAndBillingDetails,
    showDomainAdminPopup: state => state.showDomainAdminPopup,
    dkimUnverifiedKey: state => state.dkimUnverifiedKey,
    dkimVerifiedKeys: state => state.dkimVerifiedKeys,
    partnerInfo: state => state.partnerInfo,
    showAutoRenew: state => state.showAutoRenew,
    isBlacklistedCountry: state =>
      isRestrictedCountry(state.purchaseAndBillingDetails?.billing_country),
    isTrialBundle: state => state.currentPlanDetails?.bundlePlanType === BUNDLE_PLAN_TYPE.TRIAL,
  },
  /**
   * Global Actions:
   * Why are there so many actions for each property?
   * Each action can dispatch more actions, or just commit a mutation.
   * We could directly commit these into the store from the component too.
   * However, for cases where some value is dependent on another value, it makes sense to dispatch.
   */
  actions: {
    theme({ commit }, payload) {
      commit("theme", payload);
    },
    trials({ commit }, payload) {
      commit("trials", payload);
    },
    view({ commit }, payload) {
      commit("view", payload);
    },
    locale({ commit }, payload) {
      commit("locale", payload);
    },
    isAppBannerVisible({ commit }, payload) {
      commit("isAppBannerVisible", payload);
    },
    partnerId({ commit }, payload) {
      commit("partnerId", payload);
    },
    partnerName({ commit }, payload) {
      commit("partnerName", payload);
    },
    brandDetails({ commit }, payload) {
      commit("brandDetails", payload);
    },
    currentPlanDetails({ commit }, payload) {
      commit("currentPlanDetails", payload);
    },
    renewalChargeDetails({ commit }, payload) {
      commit("renewalChargeDetails", payload);
    },
    currentDomainMailboxDetails({ commit }, payload) {
      commit("currentDomainMailboxDetails", payload);
    },
    purchaseAndBillingDetails({ commit }, payload) {
      commit("purchaseAndBillingDetails", payload);
    },
    showDomainAdminPopup({ commit }, payload) {
      commit("showDomainAdminPopup", payload);
    },
    // action to handle unverified dkim key
    dkimUnverifiedKey({ commit }, payload) {
      commit("dkimUnverifiedKey", payload);
    },
    // action to handle verified dkim keys
    dkimVerifiedKeys({ commit }, payload) {
      commit(
        "dkimVerifiedKeys",
        // as we want to keep enabled key in top
        Array.isArray(payload)
          ? payload.sort((a, b) => (a.isEnabled && !b.isEnabled ? -1 : 0))
          : payload
      );
    },
    // action to add unverified key to verified array
    dkimAddToVerifiedKeyList({ commit, state }) {
      commit("dkimVerifiedKeys", [
        { ...state.dkimUnverifiedKey, isEnabled: true },
        ...state.dkimVerifiedKeys.map(d => ({ ...d, isEnabled: false })),
      ]);
      commit("dkimUnverifiedKey", null);
    },
    // action to enable provided key
    // we also mark other keys as isEnable: false
    enableDkimKey({ dispatch, state }, payload) {
      dispatch(
        "dkimVerifiedKeys",
        state.dkimVerifiedKeys.map(k => ({
          ...k,
          isEnabled: k.dkimId === payload,
        }))
      );
    },
    // action to delete dkim key from store
    deleteDkimKey({ dispatch, state }, payload) {
      dispatch(
        "dkimVerifiedKeys",
        state.dkimVerifiedKeys.filter(k => k.dkimId !== payload)
      );
    },
    partnerInfo({ commit }, payload) {
      commit("partnerInfo", payload);
    },
    showAutoRenew({ commit }, payload) {
      commit("showAutoRenew", payload);
    },
    resetState({ commit, dispatch }) {
      // Dispatch (not commit) so each module's resetState action can in turn
      // cascade into its own nested modules — root only knows top-level.
      Object.keys(stateModules).forEach(key => {
        dispatch(`${key}/resetState`);
      });
      commit("resetState");
    },
  },
  /* Global Mutations */
  mutations: {
    theme(state, payload) {
      if (payload) {
        state.theme = payload;
      }
    },
    trials(state, payload) {
      state.trials = {
        ...state.trials,
        ...payload,
      };
    },
    locale(state, payload) {
      state.locale = payload;
    },
    view(state, payload) {
      state.view = payload;
    },
    isAppBannerVisible(state, payload) {
      state.isAppBannerVisible = payload;
    },
    partnerId(state, payload) {
      state.partnerId = payload;
    },
    partnerName(state, payload) {
      state.partnerName = payload;
    },
    brandDetails(state, payload) {
      state.brandDetails = payload;
    },
    currentPlanDetails(state, payload) {
      state.currentPlanDetails = payload;
    },
    renewalChargeDetails(state, payload) {
      state.renewalChargeDetails = payload;
    },
    purchaseAndBillingDetails(state, payload) {
      state.purchaseAndBillingDetails = payload;
    },
    currentDomainMailboxDetails(state, payload) {
      state.currentDomainMailboxDetails = payload;
    },
    showDomainAdminPopup(state, payload) {
      state.showDomainAdminPopup = payload;
    },
    dkimUnverifiedKey(state, payload) {
      state.dkimUnverifiedKey = payload;
    },
    dkimVerifiedKeys(state, payload) {
      state.dkimVerifiedKeys = payload;
    },
    partnerInfo(state, payload) {
      state.partnerInfo = payload;
    },
    showAutoRenew(state, payload) {
      state.showAutoRenew = payload;
    },
    resetState(state) {
      Object.assign(state, getDefaultState(), {
        // reset everything except partner info
        partnerInfo: state.partnerInfo, // as we need partner info on login
      });
    },
  },
});
